/* eslint-disable */
import * as React from "react";
import * as Highcharts from "highcharts";
import treemapHighcharts from "highcharts/modules/treemap";
import variwideHighcharts from "highcharts/modules/variwide";
import HighchartsReact from "highcharts-react-official"; 

import { createStyles, WithStyles, withStyles } from "@material-ui/styles";
import { Theme } from "@material-ui/core";
import * as merge from "lodash.merge";
import { cloneDeep } from "lodash";

export const defaultContext: Highcharts.Options = {
  colors: ['#0F8AC0', '#1BB8C2', '#1CA955', '#E38114', '#DD5C3D', '#8F8F8F', '#D9D10C', '#2D5EDA', '#784FBA', '#D656A3'],
  plotOptions: {
    series: {
      animation: false
    }
  },
  chart: {
    animation: false,
    style: {
        fontFamily: ['Lato', 'sans-serif'].join(',')
    }
  },
  credits: {
    enabled: false,
  },
  tooltip: {
    useHTML: true,
    borderWidth: 0,
    shadow: false,
    animation: true,
    padding: 0,
    backgroundColor: "rgba(0,0,0,0)",
    style: {
        fontFamily: ['Lato', 'sans-serif'].join(',')
    }
  },
  xAxis: {
    labels: {
      style: {
        fontFamily: ['Lato', 'sans-serif'].join(','),
        fontWeight: 'bold',
      }
    }
  }
};

export const HightChartsThemeContext = React.createContext<Highcharts.Options>(defaultContext);


const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: "100%",
      minHeight: "inherit",
      minWidth: "inherit",
      width: "100%",
      display: "flex"
    }
  });

export enum ChartTooltipPositioning {
  Standard, Vertical, Horizontal
}

interface HighchartsChartProps {
  data: any;
  theme: Theme;
  tooltipPositioning?: ChartTooltipPositioning;
}

type Props = HighchartsChartProps & WithStyles<typeof styles>;

// TODO: Для Highcharts надо добавить настройки для No data.
class HighchartsChart extends React.Component<Props> {
  static contextType = HightChartsThemeContext;
  private _box = React.createRef<any>();
  private _chart = React.createRef<any>();

  componentDidMount() {
    window.dispatchEvent(new Event('resize'))
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100); 
    setTimeout(() => window.dispatchEvent(new Event('resize')), 200);  
  }
  componentDidUpdate() {
    window.dispatchEvent(new Event('resize'))
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100); 
    setTimeout(() => window.dispatchEvent(new Event('resize')), 200);  
  }

  render() {
    const { classes } = this.props;
    Highcharts.setOptions(this.context); 
    treemapHighcharts(Highcharts);
    variwideHighcharts(Highcharts);
    return (
      <div className={classes.root} ref={this._box}>
        <HighchartsReact
          ref={this._chart}
          highcharts={Highcharts}
          options={this._getChartOptions()}
          containerProps={{ className: classes.root }}
        />
      </div>
    );
  }

  private _getChartOptions = () => {
    const chartOptions: Highcharts.Options = {};
    merge(chartOptions, this.context);
    merge(chartOptions, this._getHighchartsMaterialTheme());
    merge(chartOptions, this.props.data);
    this._setTooltipPositioning(chartOptions.tooltip!);
    const result: Highcharts.Options = {};
    for (let key of Object.keys(chartOptions)) {
      if (chartOptions[key] !== undefined) {
        result[key] = chartOptions[key];
      }
    }
    return result;
  }

  private _getHighchartsMaterialTheme = () => {
    const { data, theme } = this.props;
    const xAxisStyles = {
      gridLineColor: theme.palette.action.selected,
      lineColor: theme.palette.text.hint,
      tickColor: theme.palette.text.hint,
      labels: {
        style: { color: theme.palette.text.hint }
      },
      title: {
        style: { color: theme.palette.text.primary }
      }
    };
    const yAxisStyles = {
      ...xAxisStyles,
    }
    const highchartsMaterialTheme = {
      chart: {
        backgroundColor: theme.palette.background.paper
      },
      title: {
        style: { color: theme.palette.text.primary }
      },
      subtitle: {
        style: { color: theme.palette.text.hint }
      },
      tooltip: {
        backgroundColor: theme.palette.action.selected,
        style: { color: theme.palette.text.primary },
      },
      legend: {
        itemHiddenStyle: { color: theme.palette.action.disabled },
        itemHoverStyle: { color: theme.palette.text.primary },
        itemStyle: { color: theme.palette.text.hint }
      },
      xAxis: data && this._getAxisStyles(data.xAxis, xAxisStyles),
      yAxis: data && this._getAxisStyles(data.yAxis, yAxisStyles),
    };
    return highchartsMaterialTheme;
  }

  private _getAxisStyles = (dataAxis, axisStylesTemplate) => {
    const axisStyles: any = {};
    if (!dataAxis || dataAxis.length === 0) {
      return cloneDeep(axisStylesTemplate);
    }
    if (!Array.isArray(dataAxis)) {
      return merge(dataAxis, cloneDeep(axisStylesTemplate));
    }

    for (let axisIndex = 0; axisIndex < dataAxis.length; axisIndex++) {
      if (axisIndex !== 0 && !dataAxis[axisIndex]) {
        continue;
      }
      axisStyles[axisIndex] = merge(axisStyles[axisIndex], cloneDeep(axisStylesTemplate));
    }
    return axisStyles;
  }

  private _setTooltipPositioning = (tooltipOptions: Highcharts.TooltipOptions) => {
    switch (this.props.tooltipPositioning) {
      case ChartTooltipPositioning.Vertical:
        //tooltipOptions.positioner = this._getTooltipVerticalPositioner();
        break;
      case ChartTooltipPositioning.Horizontal:
        tooltipOptions.positioner = this._getTooltipHorizontalPositioner();
        break;
      default:
      case ChartTooltipPositioning.Standard:
        break;
    }
  }

  private _getTooltipHorizontalPositioner = () => {
    const positioner = function (this: any, labelWidth, labelHeight, point) {
      let tooltipX: number;
      const chart = this.chart;
      const padding = 30;
      // Calculate safe tooltip position of the plot bounds
      tooltipX = point.plotX + chart.plotLeft + padding;
      if (tooltipX + labelWidth > chart.plotWidth) {
        tooltipX = point.plotX + chart.plotLeft - labelWidth - padding;
      }
      const widthShift = 20;
      const tooltipY =
        point.plotY + labelHeight + widthShift > chart.plotHeight
          ? point.plotY + chart.plotTop - labelHeight - widthShift + padding
          : point.plotY + chart.plotTop - padding + widthShift;

      return {
        x: tooltipX,
        y: tooltipY
      };
    };
    return positioner;
  }

  private _getTooltipVerticalPositioner = () => {
    // TODO: Не реализовано, сделать по аналогии с _getTooltipHorizontalPositioner.
  }
}

export default withStyles(styles, { withTheme: true })(HighchartsChart);
